import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, AlertTriangle, UserX, Phone, CheckCircle } from 'lucide-react';

const ChildSafety = () => {
    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white">
            {/* Header */}
            <header className="fixed top-0 w-full z-50 bg-gray-900/90 backdrop-blur-md border-b border-gray-700/50">
                <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center space-x-2">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-red-500 to-pink-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm sm:text-lg">AM</span>
                        </div>
                        <span className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                            AVENTE MATNITCH
                        </span>
                    </Link>

                    <Link
                        to="/"
                        className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-full transition-colors duration-300"
                    >
                        <ArrowLeft size={16} />
                        <span className="text-sm">Back to Home</span>
                    </Link>
                </div>
            </header>

            {/* Main Content */}
            <div className="pt-24 pb-16 px-4">
                <div className="max-w-4xl mx-auto">

                    {/* Hero Section */}
                    <div className="text-center mb-12">
                        <div className="flex items-center justify-center space-x-4 mb-6">
                            <div className="w-16 h-16 bg-gradient-to-r from-red-600 to-red-800 rounded-2xl flex items-center justify-center">
                                <ShieldAlert className="text-white" size={32} />
                            </div>
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-red-500">
                            Child Safety Center
                        </h1>
                        <p className="text-xl text-gray-300 mb-6">
                            AVENTE MATNITCH PRIVATE LIMITED has a zero-tolerance policy against Child Sexual Abuse Material (CSAM) and child endangerment.
                        </p>
                        <div className="inline-flex items-center bg-red-500/10 border border-red-500/30 rounded-full px-4 py-2 text-sm">
                            <AlertTriangle className="w-4 h-4 mr-2 text-red-400" />
                            <span className="text-red-400 font-semibold">Strict 18+ Age Requirement Across All Apps</span>
                        </div>
                    </div>

                    {/* Policy Content */}
                    <div className="space-y-8">

                        {/* Point of Contact */}
                        <div className="bg-gradient-to-r from-red-900/30 to-gray-800/80 backdrop-blur-md p-6 sm:p-8 rounded-2xl border border-red-500/50 relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/10 rounded-full blur-3xl"></div>
                            <h2 className="text-2xl font-bold text-red-400 mb-6 flex items-center">
                                <Phone className="mr-3" size={24} /> Dedicated Child Safety Point of Contact
                            </h2>
                            <p className="text-gray-300 mb-6 leading-relaxed">
                                Our dedicated Child Safety Point of Contact is fully trained and prepared to speak to AVENTE MATNITCH's CSAM prevention practices, compliance with Child Safety and Child Endangerment policies, and incident resolution.
                            </p>

                            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
                                <div className="space-y-4">
                                    <div>
                                        <p className="text-sm text-gray-400 uppercase tracking-wide mb-1">Targeted Child Safety Email</p>
                                        <a href="mailto:aventematnitch@gmail.com" className="text-xl font-bold text-white hover:text-red-400 transition-colors">
                                            aventematnitch@gmail.com
                                        </a>
                                    </div>
                                    <div className="pt-4 border-t border-gray-700/50">
                                        <p className="text-sm text-gray-400 mb-2">Our child safety contact team is available 24/7 to interface with Google, law enforcement, and recognized child protection authorities globally.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Our Practices */}
                        <div className="bg-gray-800/50 backdrop-blur-md p-6 sm:p-8 rounded-2xl border border-gray-700/50">
                            <h2 className="text-2xl font-bold text-pink-400 mb-6 flex items-center">
                                <CheckCircle className="mr-3" size={24} /> CSAM Prevention Practices
                            </h2>
                            <div className="space-y-4 text-gray-300">
                                <p>To aggressively combat child endangerment on Gupshup, Dakhla, TellMe, Guplup, and LiveGup, we deploy strict practices:</p>
                                <ul className="list-disc list-inside space-y-3 ml-2">
                                    <li><strong>Automated Scanning:</strong> All user-generated uploaded content (where applicable) is scanned automatically via advanced CSAM detection APIs upon upload.</li>
                                    <li><strong>Strict Age Gating:</strong> All applications explicitly require users to verify they are 18 years of age or older during onboarding.</li>
                                    <li><strong>Immediate Banning:</strong> Accounts suspected of CSAM-related or child-predatory behavior are permanently banned immediately without warning.</li>
                                    <li><strong>NCMEC Reporting:</strong> We actively interface and forward all confirmed CSAM materials and user metrics to law enforcement and the National Center for Missing and Exploited Children (NCMEC).</li>
                                </ul>
                            </div>
                        </div>

                        {/* User Reporting */}
                        <div className="bg-gray-800/50 backdrop-blur-md p-6 sm:p-8 rounded-2xl border border-gray-700/50">
                            <h2 className="text-2xl font-bold text-pink-400 mb-6 flex items-center">
                                <UserX className="mr-3" size={24} /> In-App Reporting Mechanics
                            </h2>
                            <div className="text-gray-300 space-y-4">
                                <p>We empower our community to help keep our network safe. Every application within the AVENTE MATNITCH ecosystem contains instantaneous reporting systems:</p>
                                <ul className="list-disc list-inside space-y-2 ml-2">
                                    <li>Dedicated "Report User" button present on all video calling interfaces and public profiles.</li>
                                    <li>"Child Endangerment" specific reporting tags routed directly to our priority moderation queue.</li>
                                    <li>100% anonymous reporting—your identity is never shared with the reported user.</li>
                                </ul>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChildSafety;
